import React from "react";
import { useDispatch } from "hooks/useDispatch";
import { useBettingFilters } from "../hooks/useBettingFilters";
import { BettingEventCard } from "./BettingEventCard";
import { toggleSelection } from "../Betslip/betslipSlice";
import type { ToggleSelectionPayload } from "hooks/firestore/betting/types";
import { isOutright } from "utilities/sharedBettingUtilities";
import { LoadMore } from "components/LoadMore";
import { defaultPageSize } from "utilities/display";
import { MatchCardSkeleton } from "./MatchCardSkeleton";
import { PageMessage } from "components/PageMessage";
import { ReactComponent as EmptyIcon } from "sections/Entries/assets/empty/upcoming.svg";
import { BettingEmptyStateActions } from "./BettingEmptyStateActions";
import type { Hub } from "hooks/graphql/useHubs";
import useRestoreScrollPosition from "hooks/useRestoreScrollPosition";
import { useBettingEvents } from "hooks/firestore/v2/betting/useBettingEvents";

import * as styles from "./BettingEventCards.module.scss";

type BettingEventCardsProps = {
  page: number;
  setPage: (newPage: number) => void;
  sportsTitles: string[];
  canSGMBet: boolean;
  hub: Hub;
};

/** Renders the betting events provided as a list of cards */
export const BettingEventCards = ({
  page,
  setPage,
  sportsTitles,
  canSGMBet,
  hub,
}: BettingEventCardsProps) => {
  const dispatch = useDispatch();

  const [filters, setFilters] = useBettingFilters();

  const { events, isLoading: eventsLoading } = useBettingEvents({
    page,
    titles: sportsTitles,
    tournaments: filters.tour,
    tab: filters.tab,
    hub: hub.slug as "sports" | "esports",
    sgm: canSGMBet && filters.sgm === "true",
  });

  useRestoreScrollPosition(eventsLoading);

  if (eventsLoading && page == 1) {
    return (
      <div className={styles.events}>
        {Array.from({ length: 8 }).map((_, index) => (
          <MatchCardSkeleton key={`match-card-skeleton-${index}`} />
        ))}
      </div>
    );
  }

  if (!eventsLoading && (events || [])?.length === 0) {
    return (
      <PageMessage
        title={"No events found"}
        subTitle={`There are currently no ${filters.sgm === "true" ? "Same Game Multi" : "Next To Go"} markets which match your filters`}
        image={<EmptyIcon />}
        className={styles.pageContainer}
      >
        <BettingEmptyStateActions />
      </PageMessage>
    );
  }

  const handleToggleSelection = (event, outcome) => {
    const payload = {
      type: isOutright(event.eventType) ? "outright" : "match",
      outcomeId: outcome.id,
      marketId: event.mainMarket.id,
      sourceId: event.eventId,
      odds: outcome.odds,
      eventId: event.eventId,
    } satisfies ToggleSelectionPayload;
    dispatch(toggleSelection(event, payload, event.hub));
  };

  return (
    <div className={styles.events}>
      {(events || []).map((event) => (
        <BettingEventCard
          key={event.eventId}
          event={event}
          toggleSelection={handleToggleSelection}
          isSelected
        />
      ))}

      <LoadMore
        hasMore={
          !eventsLoading
            ? page * defaultPageSize() <= (events || []).length
            : true
        }
        next={() => {
          if (!eventsLoading) {
            setPage(Number(page) + 1);
            setFilters({ page: Number(page) + 1 });
          }
        }}
      />
    </div>
  );
};
